import LogRocket from 'logrocket';

import _ from 'lodash';
import template from '../copyReportLink/copyReportLinkModal.html';

/* @ngInject */
export default function ModalService($q, $uibModal) {
  const service = this;
  service.confirmationModal = confirmationModal;
  service.quickfind = quickfind;
  service.openEventDetailsModalSettings = openEventDetailsModalSettings;
  service.openCopyReportLink = openCopyReportLink;
  service.openScheduler = openScheduler;
  service.openAlertPolicy = openAlertPolicy;
  service.open = open;
  service.openDateRange = openDateRange;

  /**
   * @typedef {object} ModalOptions
   * @property {string} [modalTitle]
   * @property {string} [optionalWarning]
   * @property {string} [selectionCriteria] - A brief message to describe to the user what is being confirmed
   * @property {string} [additionalOptionMessage] - Label for an additional option to confirm.
   * E.g.: A user confirms a sync *and also marks it as a 'forced sync'*
   * @property {boolean} [danger] - If the result is destructive
   * This will alter the coloring of the buttons to be neutral rather than emphasizing danger by default
   * @property {Function} [onConfirm] - Called if the user confirms
   * @property {Function} [onCancel] - Called if the modal is closed by the user.
   * ⚠️ onCancel MUST NOT perform any irreversible or destructive operation
   * as it could be inadvertently invoked should onConfirm throw an error ⚠️
   * @property {string} [typeToConfirm] - User will be required to "really" confirm by typing the given string
   */

  /**
   * @param {ModalOptions} [modalOptions] Modal config
   * @returns {Promise} The result or the result of the passed callback, if supplied in modalOptions
   */
  function confirmationModal(modalOptions = {}) {

    const scary = new RegExp(/(delet|destr|remov)/, 'i');
    const danger =
      modalOptions.danger ??
      _.some([
        modalOptions.modalTitle,
        modalOptions.optionalWarning,
        modalOptions.selectionCriteria,
        modalOptions.additionalOptionMessage,
      ], text => scary.test(text));

    const modalInstance = $uibModal.open({
      component: 'confirmationModal',
      size: 'sm',
      resolve: {
        modalOptions: () => ({
          modalTitle: 'Are you sure?',
          selectionCriteria: danger ? 'This action is irreversible' : '',
          buttonText: danger ? 'Proceed' : 'Confirm',
          danger,
          ...modalOptions
        })
      }
    });
    const { onConfirm, onCancel } = modalOptions;
    return modalInstance.result
      .then((onConfirm && ((...args) => onConfirm(...args))) || (data => data))
      .catch(onCancel || (() => $q.reject()));
  }

  let quickfindModalIsOpen = false;

  /**
   * @param {Event} [event]
   * @returns {Promise<undefined>}
   */
  async function quickfind(event) {
    // In some browsers, binding may collide
    event && event.preventDefault();

    if (quickfindModalIsOpen) {
      return false;
    }
    quickfindModalIsOpen = true;

    LogRocket.track('quickfind');
    const { result } = $uibModal.open({
      component: 'quickfindModal',
      size: 'sm'
    });
    return result.catch(_.noop).finally(() => {
      quickfindModalIsOpen = false;
    });
  }

  /*
    * site-wide modal for eventDetails
    * accessible from anywhere
    */
  function openEventDetailsModalSettings(theEvent, allEvents, loadMore, panelId) {
    return $uibModal.open({
      component: 'eventDetailsModalComponent',
      size: 'full',
      resolve: {
        theEvent: () => theEvent,
        allEvents: () => allEvents,
        loadMore: () => loadMore,
        panelId: () => panelId,
      }
    });
  }

  function openCopyReportLink(text) {
    return $uibModal.open({
      template,
      controller: 'CopyReportLinkModalController',
      controllerAs: 'CopRepLinkModCtrl',
      size: 'lg',
      resolve: { text: () => text }
    });
  }

  // TODO: refactor and use component attr of uiBootstrap to make this much simpler
  /**
   * @param {object} scheduleOptions
   * @param {string} scheduleOptions.title
   * @param {object} scheduleOptions.eq
   * @param {object} scheduleOptions.timerSettings
   * @param {Function} [scheduleOptions.enableSchedule]
   * @returns {Promise} modalInstance.result
   */
  function openScheduler(scheduleOptions) {
    const modalInstance = $uibModal.open({
      component: 'scheduleModal',
      size: 'md',
      resolve: {
        options: () => scheduleOptions
      }
    });

    return modalInstance.result;
  }

  function openAlertPolicy(notificationConfig, org) {
    return $uibModal.open({
      component: 'alertPolicyModalComponent',
      size: 'lg',
      resolve: {
        notificationConfig: () => notificationConfig,
        org: () => org
      }
    }).result;
  }

  /**
   * Generic modal $uibModal.open wrapper
   *
   * @param {object} modalOptions https://github.com/angular-ui/bootstrap/tree/master/src/modal/docs
   * @returns {Promise} Result
   */
  function open(modalOptions) {
    return $uibModal.open(modalOptions).result;
  }

  function openDateRange(daysOfDataToPull, endRangeDate) {
    const modalInstance = $uibModal.open({
      component: 'dateRangeModal',
      size: 'lg',
      resolve: {
        daysOfDataToPull: () => daysOfDataToPull,
        endRangeDate: () => endRangeDate
      }
    });
    return modalInstance.result;
  }
}

